import { Linking, StyleSheet, Text } from 'react-native'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { router } from 'expo-router'
import * as CardApplication from '@/src/experiences/card-application'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { StandaloneWrapper } from '@/src/common/wrappers/StandaloneWrapper'
import { DesignSystem } from '@/src/design-system'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Button } from '@/src/common/components/Button'
import { useEffect } from 'react'
import { formatNumber } from '@/src/common/utils/general'
import { routerBack } from '@/src/common/utils/routerBack'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useEvent } from 'tamagui'
import * as Hooks from '../../hooks'
import { REDEEM_POINTS_FOR_STATEMENT_CREDIT } from '@/src/common/gql/mutations/card'
import { useMutation } from '@apollo/client'
import { GET_POINTS_BALANCE } from '@/src/common/hooks/usePoints'

export function RedemptionConfirmation() {
  const logError = useLogError()
  const Alert = useAlert(RedemptionConfirmation.name)

  const { pointsToRedeem, remainingPoints, creditAmount, conversionRate, idempotencyKey } =
    Hooks.usePointsToStatementCredit()

  const cardPolicyAndProcedures =
    CardApplication.Statsig.ParameterStores.useCardPolicyAndProcedures()

  const remainingPointsFinishedLoading = typeof remainingPoints === 'number'

  const alertAboutInsufficientPoints = useEvent(() => {
    if (typeof remainingPoints === 'number' && remainingPoints < 0) {
      Alert.alert(
        'Insufficient Points Balance',
        `You do not have enough points to redeem ${formatNumber(Number(pointsToRedeem ?? 0) || 0)} points.`,
        [{ text: 'OK', onPress: () => void routerBack() }],
        { cancelable: false, closeableByOverlay: false, onDismiss: () => void routerBack() },
      )
    }
  })

  const [redeemPointsForStatementCredit, { loading: redeemPointsLoading }] = useMutation(
    REDEEM_POINTS_FOR_STATEMENT_CREDIT,
    { refetchQueries: [GET_POINTS_BALANCE] },
  )

  const handleContinue = () => {
    if (!idempotencyKey) {
      logError(new Error('Idempotency key is required but missing'))
      Alert.alert(
        'Oops, Something Went Wrong!',
        `We couldn't process your statement credit redemption at this time. Please try again later, or contact support if the issue persists.`,
        [
          {
            text: 'Contact Support',
            onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
            style: 'default',
          },
          { text: 'Return', onPress: router.dismissAll, style: 'default' },
        ],
      )
      return
    }
    void redeemPointsForStatementCredit({
      variables: { numPoints: Number(pointsToRedeem), idempotencyKey: idempotencyKey },
      onCompleted() {
        router.push({
          pathname: '/main/points-to-statement-credit/success-confirmation',
          params: { creditAmount },
        })
      },
      onError(...args) {
        logError(...args)
        Alert.alert(
          'Oops, Something Went Wrong!',
          `We couldn't process your statement credit redemption at this time. Please try again later, or contact support if the issue persists.`,
          [
            {
              text: 'Contact Support',
              onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
              style: 'default',
            },
            { text: 'Return', onPress: router.dismissAll, style: 'default' },
          ],
        )
        return
      },
    })
  }

  useEffect(
    () => alertAboutInsufficientPoints(),
    [remainingPointsFinishedLoading, pointsToRedeem, Alert, alertAboutInsufficientPoints],
  )

  const isMissingRequiredData =
    !pointsToRedeem || !remainingPoints || !creditAmount || !conversionRate || !idempotencyKey
  const logMissingRequiredData = useEvent(() => {
    if (isMissingRequiredData) {
      logError('Missing required data for redemption confirmation', {
        pointsToRedeem,
        remainingPoints,
        creditAmount,
        conversionRate,
        idempotencyKey,
      })
    }
  })

  useEffect(() => logMissingRequiredData(), [logMissingRequiredData, isMissingRequiredData])

  if (isMissingRequiredData) {
    return null
  }

  return (
    <StandaloneWrapper
      screen="/main/points-to-statement-credit/redemption-confirmation"
      title="Confirm Redemption"
      profileProps={{ menuIconProps: { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] } }}
      hideBackButton={false}
    >
      <DesignSystem.PointsRedemption.PointsRedemptionConfirmationInfoCard
        pointsToRedeem={pointsToRedeem ? Number(pointsToRedeem) || 0 : 0}
        remainingPoints={remainingPoints ? Number(remainingPoints) || 0 : 0}
        creditAmount={creditAmount ? Number(creditAmount) || 0 : 0}
        conversionRate={conversionRate || ''}
      />

      <DesignSystem.Card style={styles.whiteBlock}>
        <Text style={typography['label/sm-regular']}>
          Statement credits will appear within{' '}
          <Text style={typography['label/sm-bold']}>24 hours</Text> and cannot be used to pay a
          Minimum Payment Due.
        </Text>
        <Text style={typography['label/sm-regular']}>
          Remember to pay the Minimum Payment Due on your Card by the due date.
        </Text>
        <Text
          style={[
            typography['label/sm-regular'],
            { color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] },
          ]}
          onPress={() =>
            void openInAppBrowser(
              cardPolicyAndProcedures.get(
                'cardholder_rewards_terms_of_use',
                'https://mesamember.com/legal/mesa-membership-terms-and-conditions',
              ),
              'Terms and Conditions',
            )
          }
        >
          Terms and conditions
        </Text>
      </DesignSystem.Card>

      <Button
        title="Use Points"
        onPress={handleContinue}
        disabled={
          redeemPointsLoading ||
          remainingPoints === 'loading' ||
          !pointsToRedeem ||
          !remainingPoints ||
          !creditAmount ||
          !conversionRate ||
          !idempotencyKey ||
          (Number(remainingPoints) || 0) < 0
        }
        loading={redeemPointsLoading}
      />
    </StandaloneWrapper>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
    paddingHorizontal: 14,
    gap: 32,
    color: mesaColorNames['global/grey/80 #2F3133'],
  },
  whiteBlock: {
    borderRadius: 12,
    padding: 16,
    gap: 24,
  },
  infoContainer: {
    gap: 16,
  },
  infoSection: {
    gap: 8,
    padding: 4,
  },
  infoSectionBorder: {
    borderBottomWidth: 1,
    borderBottomColor: mesaColorNames['semantic/bg/disabled #DADDDE'],
    paddingBottom: 16,
  },
})
